import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ChevronDown,
  Link2,
  Plus,
  Clapperboard,
  PanelsTopLeft,
  MicVocal,
  AudioLines,
  NotebookText,
  Loader2,
  Sparkle,
  Upload,
  ImagePlus,
  Film,
  Sparkles
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { appFetch, uploadFile, appApiBaseUrl } from '@/shared/api/app-client'
import type { ApiResponse, PaginatedResponse, ProjectListItem, AssetUploadResult, Template } from '@/shared/api/app-types'
import { projectTypeLabel } from './projects-canvas'
import { cn } from '@/shared/ui/cn'
import { WorkspacePickerDialog } from '@/features/app-shell/components/workspace-picker-dialog'
import {
  buildFileProjectForEditor,
  buildSourceProjectForEditor,
  ensureClipWorkspaceRoot,
} from '@/features/app-shell/utils/clip-render'

/** 将 /uploads/... 相对路径补全为后端完整 URL */
function resolveCoverUrl(url: string | null): string | null {
  if (!url) return null
  if (url.startsWith('/uploads/')) {
    return `${appApiBaseUrl}${url}`
  }
  return url
}

type ToolEntry = {
  id: string
  label: string
  icon: typeof Clapperboard
  comingSoon?: boolean
}

const tools: ToolEntry[] = [
  { id: 'caption', label: '添加字幕', icon: Clapperboard },
  { id: 'editor', label: '编辑视频', icon: PanelsTopLeft },
  { id: 'voiceover', label: 'AI 配音', icon: MicVocal },
  { id: 'voice-clone', label: '语音克隆', icon: AudioLines },
  { id: 'image-gen', label: 'AI 生图', icon: ImagePlus },
  { id: 'transcript', label: '转写', icon: NotebookText },
  { id: 'video-gen', label: 'AI生成视频', icon: Film },
  { id: 'digital-human', label: '数字人', icon: Sparkles },
  // 暂未实现的工具，后续再开放
  // { id: 'audiogram', label: '音频图', icon: Volume2 },
  // { id: 'autoframe', label: '自动构图', icon: Sparkles },
  // { id: 'denoise', label: '降噪', icon: WandSparkles },
  // { id: 'eye-contact', label: '眼神矫正', icon: Sparkles },
]

const languages = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: '英语' },
  { value: 'ja', label: '日语' },
  { value: 'ko', label: '韩语' },
  { value: 'es', label: '西班牙语' },
  { value: 'fr', label: '法语' },
  { value: 'de', label: '德语' },
  { value: 'pt', label: '葡萄牙语' },
  { value: 'hi', label: '印地语' },
  { value: 'ar', label: '阿拉伯语' },
]

const resolutions = [
  { value: '720', label: '720p' },
  { value: '1080', label: '1080p' },
  { value: '4k', label: '4K' },
]

export type CaptionStyleOption = {
  key: string
  title: string
}

/** 字幕样式预览资源基础地址（本地静态文件） */
export const TEMPLATE_BASE = '/templates'

export const captionLibraryStyles: CaptionStyleOption[] = [
  { key: 'blue', title: '蓝色' },
  { key: 'deepdiver', title: '深海' },
  { key: 'popline', title: '波普' },
  { key: 'beasty', title: '野兽' },
  { key: 'phantom', title: '幻影' },
  { key: 'playdate', title: '游戏机' },
  { key: 'galaxy', title: '星河' },
  { key: 'turban', title: '头巾' },
  { key: 'flipper', title: '翻转' },
  { key: 'spell', title: '咒语' },
  { key: 'youshaei', title: '尤沙伊' },
  { key: 'podp', title: '播客P' },
  { key: 'noah', title: '诺亚' },
  { key: 'drive', title: '驱动' },
  { key: 'orange', title: '橙色' },
  { key: 'ghost', title: '幽灵' },
  { key: 'probox', title: '专业框' },
  { key: 'webster', title: '韦伯斯特' },
  { key: 'lumina', title: '流光' },
  { key: 'indigo', title: '靛蓝' },
  { key: 'ember', title: '余烬' },
  { key: 'glow', title: '辉光' },
  { key: 'impact', title: '冲击' },
  { key: 'notes', title: '笔记' },
  { key: 'vintage', title: '复古' },
  { key: 'mint', title: '薄荷' },
  { key: 'onepunch', title: '一拳' },
  { key: 'silka', title: '丝卡' },
  { key: 'headlines', title: '头条' },
  { key: 'wasabi', title: '芥末' },
  { key: 'emberduo', title: '双焰' },
  { key: 'pet', title: '宠物' },
  { key: 'zen', title: '禅意' },
  { key: 'mozi', title: '墨子' },
  { key: 'techtalk', title: '科技访谈' },
  { key: 'yc', title: 'YC' },
  { key: 'playfair', title: '游乐场' },
  { key: 'popping', title: '弹出' },
  { key: 'wiggle', title: '摆动' },
  { key: 'typewriter', title: '打字机' },
  { key: 'wavy', title: '波浪' },
  { key: 'kinetictypography', title: '动态排版' },
  { key: 'ticker', title: '滚动条' },
  { key: 'squiggly', title: '波浪线' },
  { key: 'glitchv2', title: '故障V2' },
  { key: 'glitch', title: '故障' },
  { key: 'striker', title: ' striker' },
  { key: 'trophy', title: '奖杯' },
  { key: 'prism', title: '棱镜' },
  { key: 'halo', title: '光环' },
  { key: 'crimson', title: '深红' },
  { key: 'candy', title: '糖果' },
  { key: 'thinkmedia', title: '思媒' },
  { key: 'march', title: '行进' },
  { key: 'pulse', title: '脉冲' },
  { key: 'slidedown', title: '滑落' },
  { key: 'punch', title: '重击' },
  { key: 'lemon', title: '柠檬' },
  { key: 'surge', title: '涌动' },
  { key: 'bubblegum', title: '泡泡糖' },
  { key: 'cloudfall', title: '云落' },
  { key: 'tremor', title: '震颤' },
  { key: 'stretchy', title: '拉伸' },
  { key: 'bloom', title: '绽放' },
  { key: 'hype', title: '炒作' },
  { key: 'blur', title: '模糊' },
]

function getTemplateCaptionStyleKey(config: Record<string, unknown> | null | undefined): string | null {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return null
  const value = config.captionStyleKey
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

export function CaptionStyleLibraryCard({
  style,
  selected,
  onSelect,
}: {
  style: CaptionStyleOption
  selected: boolean
  onSelect: (key: string) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(style.key)}
      className={cn(
        'flex w-[150px] flex-col rounded-xl cursor-pointer transition-shadow hover:shadow-md',
        selected ? 'ring-2 ring-primary' : 'ring-1 ring-border',
      )}
    >
      <div className="h-[62px] overflow-hidden rounded-t-xl bg-muted">
        <video
          loop
          playsInline
          muted
          preload="none"
          poster={`${TEMPLATE_BASE}/${style.key}.jpg`}
          className="h-full w-full object-cover"
          onMouseEnter={(e) => {
            const v = e.currentTarget
            v.play().catch(() => {})
          }}
          onMouseLeave={(e) => {
            const v = e.currentTarget
            v.pause()
          }}
        >
          <source src={`${TEMPLATE_BASE}/${style.key}.mp4`} type="video/mp4" />
        </video>
      </div>
      <div className="flex flex-1 items-center justify-center rounded-b-xl bg-card py-1.5">
        <h3 className="text-xs font-medium text-foreground">{style.title}</h3>
      </div>
    </button>
  )
}

export function HomeCanvas({
  projectType,
}: {
  projectType?: 'clip' | 'caption'
}) {
  if (projectType === 'caption') {
    return <CaptionCreateCanvas />
  }
  return <ClipHomeCanvas />
}

/* ------------------------------------------------------------------ */
/* Clip 创建页（默认 Home）                                            */
/* ------------------------------------------------------------------ */

function ClipHomeCanvas() {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [sourceUrl, setSourceUrl] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [storageSummary, setStorageSummary] = useState<{ maxUploadBytes: number; maxClippingInputSeconds: number; } | null>(null)
  const [aiAvailable, setAiAvailable] = useState(true)
  const [toolDialog, setToolDialog] = useState<{
    title: string
    description: string
  } | null>(null)
  const [editorEntryDialogOpen, setEditorEntryDialogOpen] = useState(false)
  const [workspacePickerOpen, setWorkspacePickerOpen] = useState(false)
  const [creatingEditorProject, setCreatingEditorProject] = useState(false)
  const [pendingEditorSource, setPendingEditorSource] = useState<string | null>(null)
  const [pendingEditorFileHandle, setPendingEditorFileHandle] = useState<FileSystemFileHandle | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const res = await appFetch<ApiResponse<PaginatedResponse<ProjectListItem>>>(
          '/api/projects?page=1&pageSize=4',
        )
        if (!cancelled) setProjects(res.data.items)
      } catch {
        if (!cancelled) setProjects([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    void Promise.all([
      appFetch<ApiResponse<{ maxUploadBytes: number; maxClippingInputSeconds: number }>>('/api/storage/summary'),
      appFetch<{ authenticated: boolean; session: { membership?: { quotas?: { ai_processing_seconds_remaining?: number; raw?: { ai_processing_seconds?: number } } } } | null }>('/api/auth/me'),
    ]).then(([storage, membership]) => {
      setStorageSummary(storage.data)
      const remaining = membership.session?.membership?.quotas?.raw?.ai_processing_seconds ?? membership.session?.membership?.quotas?.ai_processing_seconds_remaining
      setAiAvailable(remaining == null || Number(remaining) > 0)
    }).catch(() => undefined)
  }, [])

  // ---- 上传/链接阶段 ----

  const goToConfig = (videoSrc: string, title?: string) => {
    navigate({
      to: '/app/clip-config',
      search: { src: videoSrc, title },
    })
  }

  const handleCreateFromUrl = () => {
    if (!aiAvailable) { setError('AI 切片额度已用完，升级后继续。'); return }
    if (!sourceUrl.trim()) {
      setError('请粘贴视频链接')
      return
    }
    goToConfig(sourceUrl.trim())
  }

  const handleFileSelect = async (file: File | undefined) => {
    if (!file) return
    if (!aiAvailable) { setError('AI 切片额度已用完，升级后继续。'); return }
    if (storageSummary && file.size > storageSummary.maxUploadBytes) { setError(`单文件不能超过 ${(storageSummary.maxUploadBytes / 1024 / 1024).toFixed(0)}MB。`); return }
    setUploading(true)
    setUploadProgress(0)
    setError(null)
    try {
      const res = await uploadFile<ApiResponse<{ src: string; durationSeconds: number; fileName: string }>>(
        '/api/clipping/sources/upload',
        file,
        (percent) => setUploadProgress(percent),
      )
      // 上传完成 → 跳到配置页
      goToConfig(res.data.src, res.data.fileName)
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传文件失败')
    } finally {
      setUploading(false)
    }
  }

  const handleCreateEditorProjectFromSource = async (source: string) => {
    if (!source.trim() || creatingEditorProject) return

    setCreatingEditorProject(true)
    setError(null)
    try {
      const wsStatus = await ensureClipWorkspaceRoot()
      if (!wsStatus.ok) {
        setPendingEditorSource(source)
        setPendingEditorFileHandle(null)
        setWorkspacePickerOpen(true)
        return
      }

      const result = await buildSourceProjectForEditor({ src: source.trim() })
      navigate({
        to: '/editor/$projectId',
        params: { projectId: result.projectId },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建视频编辑项目失败')
    } finally {
      setCreatingEditorProject(false)
    }
  }

  const handleCreateEditorProjectFromFile = async (handle: FileSystemFileHandle) => {
    if (creatingEditorProject) return

    setCreatingEditorProject(true)
    setError(null)
    try {
      const wsStatus = await ensureClipWorkspaceRoot()
      if (!wsStatus.ok) {
        setPendingEditorSource(null)
        setPendingEditorFileHandle(handle)
        setWorkspacePickerOpen(true)
        return
      }

      const result = await buildFileProjectForEditor({ handle })
      navigate({
        to: '/editor/$projectId',
        params: { projectId: result.projectId },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建视频编辑项目失败')
    } finally {
      setCreatingEditorProject(false)
    }
  }

  const handlePickEditorFile = async () => {
    try {
      const [handle] = await window.showOpenFilePicker({
        multiple: false,
        types: [
          {
            description: '视频文件',
            accept: {
              'video/*': ['.mp4', '.mov', '.mkv', '.webm', '.m4v', '.avi'],
            },
          },
        ],
      })

      if (!handle) return
      setEditorEntryDialogOpen(false)
      void handleCreateEditorProjectFromFile(handle)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return
      }
      setError(err instanceof Error ? err.message : '选择视频文件失败')
    }
  }

  const handleToolClick = (toolId: (typeof tools)[number]['id']) => {
    switch (toolId) {
      case 'caption':
      case 'transcript':
        navigate({ to: '/app', search: { 'project-type': 'caption' } })
        return
      case 'editor':
        if (sourceUrl.trim()) {
          void handleCreateEditorProjectFromSource(sourceUrl.trim())
          return
        }
        setEditorEntryDialogOpen(true)
        return
      case 'autoframe':
        if (sourceUrl.trim()) {
          goToConfig(sourceUrl.trim())
          return
        }
        navigate({ to: '/app' })
        return
      case 'voiceover':
        navigate({ to: '/app/tools/voiceover' })
        return
      case 'voice-clone':
        navigate({ to: '/app/tools/voice-clone' })
        return
      case 'image-gen':
        navigate({ to: '/app/tools/image-gen' })
        return
      case 'video-gen':
        navigate({ to: '/app/tools/video-gen' })
        return
      case 'digital-human':
        navigate({ to: '/app/tools/digital-human' })
        return
      default:
        return
    }
  }

  // ============ 默认上传/链接页 ============
  return (
    <>
      <Dialog open={toolDialog !== null} onOpenChange={(open) => { if (!open) setToolDialog(null) }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{toolDialog?.title}</DialogTitle>
          </DialogHeader>
          <div className="text-sm leading-6 text-muted-foreground">
            {toolDialog?.description}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={editorEntryDialogOpen} onOpenChange={setEditorEntryDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>进入视频编辑</DialogTitle>
            <DialogDescription>
              视频编辑优先走专业编辑器项目。你可以继续编辑已有项目，或者新建一个空白视频编辑项目。
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setEditorEntryDialogOpen(false)
                navigate({ to: '/projects' })
              }}
              className="rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-accent"
            >
              <div className="text-sm font-medium text-foreground">继续编辑已有项目</div>
              <div className="mt-1 text-xs leading-5 text-muted-foreground">
                进入专业编辑项目列表，继续处理本地工作区中的视频编辑项目。
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setEditorEntryDialogOpen(false)
                navigate({ to: '/app/projects', search: { type: 'video_cut' } })
              }}
              className="rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-accent"
            >
              <div className="text-sm font-medium text-foreground">查看统一项目列表</div>
              <div className="mt-1 text-xs leading-5 text-muted-foreground">
                回到 JIAN 总项目页，筛选查看“视频剪辑”类项目，保持和片段、字幕项目一致的入口认知。
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setEditorEntryDialogOpen(false)
                navigate({ to: '/projects/new' })
              }}
              className="rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-accent"
            >
              <div className="text-sm font-medium text-foreground">新建空白视频编辑项目</div>
              <div className="mt-1 text-xs leading-5 text-muted-foreground">
                创建一个新的专业编辑项目，再进入编辑器开始剪辑。
              </div>
            </button>
            <button
              type="button"
              onClick={() => { void handlePickEditorFile() }}
              className="rounded-xl border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-accent"
            >
              <div className="text-sm font-medium text-foreground">上传视频并开始编辑</div>
              <div className="mt-1 text-xs leading-5 text-muted-foreground">
                直接选择本地视频文件，自动创建专业编辑项目并把素材放到时间线中。
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <WorkspacePickerDialog
        open={workspacePickerOpen}
        onOpenChange={setWorkspacePickerOpen}
        onSuccess={() => {
          if (pendingEditorFileHandle) {
            const handle = pendingEditorFileHandle
            setPendingEditorFileHandle(null)
            void handleCreateEditorProjectFromFile(handle)
            return
          }
          if (pendingEditorSource) {
            const source = pendingEditorSource
            setPendingEditorSource(null)
            void handleCreateEditorProjectFromSource(source)
          }
        }}
      />
      <div className="mx-auto max-w-[548px] px-6 py-[45px]">
      {/* hero title */}
      <h1 className="text-center text-2xl font-semibold leading-[1.3] text-foreground">
        把长视频变成{' '}
        <span className="text-primary">爆款短视频</span>
      </h1>

      {/* link input bar */}
      <div className="group relative mt-6 w-full rounded-full bg-card shadow-sm ring-1 ring-inset ring-border transition-all hover:ring-primary/30">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
          <Link2 className="size-4 text-muted-foreground" />
        </div>
        <input
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFromUrl() }}
          className="block w-full rounded-full border-0 bg-transparent py-3 pl-11 pr-[120px] text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          placeholder="粘贴视频链接"
        />
        <button
          type="button"
          onClick={handleCreateFromUrl}
          disabled={!aiAvailable}
          className="absolute right-1.5 top-1/2 inline-flex -translate-y-1/2 items-center gap-x-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          获取片段
        </button>
      </div>

      {/* supports row */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="text-xs font-medium text-muted-foreground">
          建议时长大于 <span className="font-semibold text-foreground">1 分钟</span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="hidden sm:inline">支持</span>
          <div className="flex items-center gap-2.5 opacity-80">
            <span>▶</span>
            <span>◉</span>
            <span>𝕏</span>
            <span>▼</span>
          </div>
        </div>
      </div>

      {/* OR divider */}
      <div className="mt-4 flex justify-center">
        <span className="rounded-full bg-card px-3 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          或
        </span>
      </div>

      {/* upload dropzone */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files?.[0])}
      />
      {uploading ? (
        <div className="mt-4 rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-foreground">上传中…</span>
            <span className="font-semibold text-primary">{uploadProgress}%</span>
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-2 rounded-full bg-primary transition-all"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      ) : (
        <div
          onClick={() => aiAvailable && fileInputRef.current?.click()}
          onDrop={(e) => { e.preventDefault(); handleFileSelect(e.dataTransfer.files[0]) }}
          onDragOver={(e) => e.preventDefault()}
          className={`group mt-4 flex items-center justify-center gap-4 rounded-2xl border border-dashed border-border p-4 transition-all ${aiAvailable ? 'cursor-pointer hover:bg-card' : 'cursor-not-allowed opacity-55'}`}
        >
          <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-card text-muted-foreground transition-all group-hover:bg-accent">
            <Plus className="size-5" />
          </div>
          <div className="flex min-w-0 flex-col items-start gap-0.5 text-left">
            <span className="text-sm font-medium text-foreground">
              点击上传或拖拽文件到此处
            </span>
            <span className="text-[11px] tracking-wide text-muted-foreground">
              MP4 · MOV · WEBM · MKV，最大 {storageSummary ? `${(storageSummary.maxUploadBytes / 1024 / 1024).toFixed(0)}MB` : '400MB'}{storageSummary ? ` · 单次最长 ${Math.floor(storageSummary.maxClippingInputSeconds / 60)} 分钟` : ''}
            </span>
          </div>
        </div>
      )}

      {error ? (
        <p className="mt-3 text-sm text-destructive">{error}</p>
      ) : null}
      {!aiAvailable ? <p className="mt-3 text-sm text-destructive">AI 切片额度已用完，升级后继续。</p> : null}

      {/* clip from your AI bar */}
      <div className="mx-auto mt-8 flex max-w-[548px] flex-wrap items-center justify-center gap-x-3 gap-y-2 rounded-2xl border border-border bg-card px-4 py-3">
        <span className="inline-flex items-center rounded-full border border-transparent bg-accent px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-foreground">
          <span className="mr-1.5 size-1.5 rounded-full bg-primary" />
          新功能
        </span>
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          用你的 AI 剪辑
        </span>
        <div className="flex items-center gap-1">
          {/* ChatGPT */}
          <button type="button" aria-label="设置 ChatGPT" title="设置 ChatGPT" className="flex size-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent">
            <svg viewBox="0 0 24 24" fill="currentColor" className="size-5" style={{ color: 'rgb(255,255,255)' }}>
              <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
            </svg>
          </button>
          {/* Claude */}
          <button type="button" aria-label="设置 Claude" title="设置 Claude" className="flex size-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent">
            <svg viewBox="0 0 24 24" fill="currentColor" className="size-5" style={{ color: 'rgb(217,119,87)' }}>
              <path d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z" />
            </svg>
          </button>
          {/* Cursor */}
          <button type="button" aria-label="设置 Cursor" title="设置 Cursor" className="flex size-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent">
            <svg viewBox="0 0 24 24" fill="currentColor" className="size-5" style={{ color: 'rgb(245,245,245)' }}>
              <path d="M11.503.131 1.891 5.678a.84.84 0 0 0-.42.726v11.188c0 .3.162.575.42.724l9.609 5.55a1 1 0 0 0 .998 0l9.61-5.55a.84.84 0 0 0 .42-.724V6.404a.84.84 0 0 0-.42-.726L12.497.131a1.01 1.01 0 0 0-.996 0M2.657 6.338h18.55c.263 0 .43.287.297.515L12.23 22.918c-.062.107-.229.064-.229-.06V12.335a.59.59 0 0 0-.295-.51l-9.11-5.257c-.109-.063-.064-.23.061-.23" />
            </svg>
          </button>
          {/* OpenClaw */}
          <button type="button" aria-label="设置 OpenClaw" title="设置 OpenClaw" className="flex size-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-accent">
            <svg viewBox="0 0 120 120" fill="none" className="size-5">
              <path d="M60 10 C30 10 15 35 15 55 C15 75 30 95 45 100 L45 110 L55 110 L55 100 C55 100 60 102 65 100 L65 110 L75 110 L75 100 C90 95 105 75 105 55 C105 35 90 10 60 10Z" fill="#ff4d4d" />
              <path d="M20 45 C5 40 0 50 5 60 C10 70 20 65 25 55 C28 48 25 45 20 45Z" fill="#ff4d4d" />
              <path d="M100 45 C115 40 120 50 115 60 C110 70 100 65 95 55 C92 48 95 45 100 45Z" fill="#ff4d4d" />
              <path d="M45 15 Q35 5 30 8" stroke="#ff4d4d" strokeWidth="3" strokeLinecap="round" />
              <path d="M75 15 Q85 5 90 8" stroke="#ff4d4d" strokeWidth="3" strokeLinecap="round" />
              <circle cx="45" cy="35" r="6" fill="#050810" />
              <circle cx="75" cy="35" r="6" fill="#050810" />
              <circle cx="46" cy="34" r="2.5" fill="#00e5cc" />
              <circle cx="76" cy="34" r="2.5" fill="#00e5cc" />
            </svg>
          </button>
        </div>
      </div>

      {/* more tools */}
      <div className="mt-10 flex items-center gap-4">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          更多工具
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <div className="mt-6 flex flex-wrap justify-center gap-x-11 gap-y-6">
        {tools.map((tool) => {
          const Icon = tool.icon
          return (
            <button
              key={tool.label}
              type="button"
              onClick={() => handleToolClick(tool.id)}
              className={cn(
                'relative flex min-w-[84px] flex-col items-center justify-center gap-2',
                'cursor-pointer',
              )}
            >
              <div className="rounded-full bg-card p-4 shadow-sm transition-colors hover:bg-accent">
                {tool.id === 'editor' && creatingEditorProject ? (
                  <Loader2 className="size-5 animate-spin text-foreground" />
                ) : (
                  <Icon className="size-5 text-foreground" />
                )}
              </div>
              {tool.comingSoon ? (
                <div className="absolute right-[-25px] top-[5px] rounded-md bg-muted px-1 py-0.5 shadow-sm">
                  <span className="whitespace-nowrap text-[8px] font-medium text-muted-foreground">
                    即将上线
                  </span>
                </div>
              ) : null}
              <p className="text-xs font-medium text-foreground">{tool.label}</p>
            </button>
          )
        })}
      </div>

      {/* recent projects */}
      <div className="mt-[71px] flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">最近项目</h2>
        <Link
          to="/app/projects"
          className="inline-flex h-8 items-center justify-center gap-2 rounded-full border border-border bg-card px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent"
        >
          查看全部
        </Link>
      </div>

      {/* recent projects list */}
      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            加载中…
          </div>
        ) : projects.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center text-sm text-muted-foreground">
            <Clapperboard className="size-8 opacity-40" />
            <p>暂无项目，开始创建你的第一个项目吧</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {projects.map((project) => (
              <li key={project.id}>
                <Link
                  to="/app/projects/$projectId"
                  params={{ projectId: String(project.id) }}
                  className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 transition-colors hover:bg-accent"
                >
                  <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded bg-muted">
                    {resolveCoverUrl(project.coverUrl) ? (
                      <img src={resolveCoverUrl(project.coverUrl)!} alt={project.title} className="size-full object-cover" />
                    ) : (
                      <Clapperboard className="size-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {project.title}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{project.projectNo}</span>
                      {project.durationMs ? (
                        <span>· {Math.round(project.durationMs / 1000)}秒</span>
                      ) : null}
                      <span>· {new Date(project.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <span className="inline-block shrink-0 rounded-md bg-primary/15 px-1.5 py-0.5 text-xs font-medium text-primary">
                    {projectTypeLabel(project.type)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      </div>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Caption 创建页                                                      */
/* ------------------------------------------------------------------ */

function CaptionCreateCanvas() {
  const navigate = useNavigate()
  const [sourceUrl, setSourceUrl] = useState('')
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null)
  const [styleDialogOpen, setStyleDialogOpen] = useState(false)
  const [pendingStyle, setPendingStyle] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'caption-styles' | 'brand-templates'>('caption-styles')
  const [brandTemplates, setBrandTemplates] = useState<Template[]>([])
  const [brandTemplatesLoading, setBrandTemplatesLoading] = useState(false)
  const [brandTemplatesError, setBrandTemplatesError] = useState<string | null>(null)
  const [selectedBrandTemplateId, setSelectedBrandTemplateId] = useState<number | null>(null)
  const [language, setLanguage] = useState('zh')
  const [translateTo, setTranslateTo] = useState('none')
  const [resolution, setResolution] = useState('720')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<{ name: string; url: string; file: File } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (styleDialogOpen) {
      setPendingStyle(selectedStyle)
    }
  }, [selectedStyle, styleDialogOpen])

  useEffect(() => {
    let cancelled = false
    setBrandTemplatesLoading(true)
    setBrandTemplatesError(null)

    void appFetch<ApiResponse<PaginatedResponse<Template>>>('/api/templates?templateScope=brand&page=1&pageSize=50')
      .then((res) => {
        if (!cancelled) setBrandTemplates(res.data.items)
      })
      .catch((err) => {
        if (!cancelled) setBrandTemplatesError(err instanceof Error ? err.message : '加载品牌模板失败')
      })
      .finally(() => {
        if (!cancelled) setBrandTemplatesLoading(false)
      })

    return () => { cancelled = true }
  }, [])

  const handleFileSelect = async (file: File | undefined) => {
    if (!file) return
    setUploading(true)
    setError(null)
    setUploadedFile(null)
    try {
      const res = await uploadFile<ApiResponse<AssetUploadResult>>('/api/assets/upload', file)
      setUploadedFile({ name: res.data.fileName, url: res.data.publicUrl, file })
    } catch (err) {
      setError(err instanceof Error ? err.message : '上传文件失败')
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const inputUrl = sourceUrl.trim()
    if (!uploadedFile && !inputUrl) {
      setError('请粘贴公开视频直链或上传视频文件。')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const response = await appFetch<ApiResponse<{ taskId: string }>>('/api/clipping/analyze', {
          method: 'POST',
          headers: { 'X-Client-Request-Id': crypto.randomUUID() },
          body: JSON.stringify({
            // 上传素材和公开视频直链都走同一条“字幕项目”链路。这样转录结果、
            // 所选字幕样式、品牌模板和可分享版本会一起保存在项目内。
            src: uploadedFile?.url ?? inputUrl,
            sourceTitle: uploadedFile?.name,
            language,
            translateTo: translateTo !== 'none' ? translateTo : undefined,
            resolution,
            captionStyle: selectedStyle ?? undefined,
            brandTemplateId: selectedBrandTemplateId ?? undefined,
            transcriptOnly: true,
          }),
        })
      navigate({ to: '/app/caption-results', search: { taskId: response.data.taskId } })
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建转录任务失败')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <div className="mx-auto max-w-[548px] px-5 py-[45px] sm:px-6">
        {/* hero title */}
        <h1 className="text-center text-lg font-bold leading-[1.3] text-foreground">
          为你的短视频、Reels 和 Stories 添加字幕
        </h1>

        <form onSubmit={handleSubmit} className="mt-[26px] space-y-5">
          {/* 视频链接 */}
          <div className="space-y-2">
            <div className="group relative w-full rounded-full bg-card shadow-sm ring-1 ring-inset ring-border transition-all hover:ring-primary/30">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                <Link2 className="size-4 text-muted-foreground" />
              </div>
              <input
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                disabled={Boolean(uploadedFile)}
                className="block w-full rounded-full border-0 bg-transparent py-2.5 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed"
                placeholder="粘贴可公开访问的视频直链"
              />
            </div>
            <p className="text-xs font-medium leading-normal text-muted-foreground">
              支持可公开访问的视频直链；会按实际视频时长扣减“转录与字幕”额度。
            </p>
          </div>

          {/* 或 */}
          <div className="relative mt-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-4 text-sm font-semibold text-muted-foreground">
                或
              </span>
            </div>
          </div>

          {/* 上传区 */}
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files?.[0])}
          />
          {uploadedFile ? (
            <div className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex size-8 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-500">
                  <Clapperboard className="size-4" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-foreground">
                    {uploadedFile.name}
                  </div>
                  <div className="text-xs text-muted-foreground">上传完成</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { setUploadedFile(null) }}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                移除
              </button>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDrop={(e) => { e.preventDefault(); handleFileSelect(e.dataTransfer.files[0]) }}
              onDragOver={(e) => e.preventDefault()}
              className="group mt-6 flex cursor-pointer flex-col items-center gap-3 rounded-lg border border-dashed border-border px-4 py-9 transition-colors hover:bg-card"
            >
              <div className="flex size-8 items-center justify-center rounded-full bg-card text-muted-foreground transition-all group-hover:bg-accent">
                {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
              </div>
              <p className="text-center text-sm leading-6 text-muted-foreground">
                {uploading ? '上传中…' : '拖拽文件到此处上传'}
              </p>
              <Button
                type="button"
                className="h-8 gap-x-2 rounded-full px-4 text-xs font-medium"
              >
              选择文件
            </Button>
            <p className="text-xs text-muted-foreground">最多 15 分钟；免费用户总空间 100 MB</p>
            </div>
          )}

          {/* 字幕样式 / 品牌模板 tabs */}
          <div className="mt-4">
            <div className="flex justify-between border-b border-border pt-[5.5px]">
              <div className="flex gap-6">
                <button
                  type="button"
                  onClick={() => setActiveTab('caption-styles')}
                  className={cn(
                    'whitespace-nowrap py-1 text-sm font-medium transition-colors',
                    activeTab === 'caption-styles'
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  字幕样式
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('brand-templates')}
                  className={cn(
                    'whitespace-nowrap py-1 text-sm font-medium transition-colors',
                    activeTab === 'brand-templates'
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  品牌模板
                </button>
              </div>
            </div>

            {activeTab === 'caption-styles' ? (
              <div className="mt-4 flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
                <div className="flex items-center gap-3">
                  {(() => {
                    const selected = selectedStyle ? captionLibraryStyles.find((s) => s.key === selectedStyle) : null
                    return selected ? (
                      <img
                        src={`${TEMPLATE_BASE}/${selected.key}.jpg`}
                        alt={selected.title}
                        className="size-9 rounded-md object-cover"
                      />
                    ) : (
                      <div className="flex size-9 items-center justify-center rounded-md border border-dashed border-border text-[10px] text-muted-foreground">无</div>
                    )
                  })()}
                  <div>
                    <div className="text-sm font-semibold text-foreground">
                      {captionLibraryStyles.find((s) => s.key === selectedStyle)?.title ?? '未选择'}
                    </div>
                    <div className="text-xs text-muted-foreground">未选择时不会自动添加字幕样式</div>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStyleDialogOpen(true)}
                  className="h-8 rounded-full px-4 text-xs font-medium"
                >
                  选择样式
                </Button>
              </div>
            ) : (
              <div className="mt-4">
                {brandTemplatesLoading ? (
                  <div className="rounded-lg border border-dashed border-border bg-card px-4 py-9 text-center text-sm text-muted-foreground">
                    加载品牌模板中...
                  </div>
                ) : brandTemplatesError ? (
                  <div className="rounded-lg border border-dashed border-destructive/40 bg-destructive/5 px-4 py-9 text-center text-sm text-destructive">
                    {brandTemplatesError}
                  </div>
                ) : brandTemplates.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border bg-card px-4 py-9 text-center text-sm text-muted-foreground">
                    还没有可用的品牌模板，先去品牌模板页创建一个。
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBrandTemplateId(null)
                        setSelectedStyle(null)
                      }}
                      className={cn(
                        'rounded-xl border border-dashed bg-card p-3 text-left transition-all',
                        selectedBrandTemplateId === null
                          ? 'border-primary ring-2 ring-primary/70'
                          : 'border-border hover:border-border/80 hover:bg-accent/20',
                      )}
                    >
                      <div className="text-sm font-semibold text-foreground">不使用品牌模板</div>
                      <div className="mt-1 text-xs text-muted-foreground">本次不叠加品牌元素或模板字幕风格。</div>
                    </button>
                    {brandTemplates.map((template) => {
                      const styleKey = getTemplateCaptionStyleKey(template.config) ?? 'blue'
                      const style = captionLibraryStyles.find((item) => item.key === styleKey)
                      const selected = selectedBrandTemplateId === template.id
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => {
                            setSelectedBrandTemplateId(template.id)
                            setSelectedStyle(styleKey)
                          }}
                          className={cn(
                            'rounded-xl border bg-card p-3 text-left transition-all',
                            selected
                              ? 'border-primary ring-2 ring-primary/70'
                              : 'border-border hover:border-border/80 hover:bg-accent/20',
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <img
                              src={`${TEMPLATE_BASE}/${styleKey}.jpg`}
                              alt={template.templateName}
                              className="h-14 w-20 rounded-md object-cover"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-semibold text-foreground">{template.templateName}</div>
                              <div className="mt-1 text-xs text-muted-foreground">{style?.title ?? styleKey}</div>
                              <div className="mt-2 text-[11px] text-muted-foreground">会保存到当前字幕项目中</div>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 设置区 */}
          <div className="mt-4 space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">语言</label>
                <div className="relative">
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="h-9 w-full appearance-none rounded-lg border border-border bg-card px-3 pr-8 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    {languages.map((l) => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">翻译为</label>
                <div className="relative">
                  <select
                    value={translateTo}
                    onChange={(e) => setTranslateTo(e.target.value)}
                    className="h-9 w-full appearance-none rounded-lg border border-border bg-card px-3 pr-8 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="none">不翻译</option>
                    {languages.map((l) => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">分辨率</label>
                <div className="relative">
                  <select
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    className="h-9 w-full appearance-none rounded-lg border border-border bg-card px-3 pr-8 text-sm text-foreground shadow-sm focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    {resolutions.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                </div>
              </div>
            </div>

          </div>

          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : null}

          {/* 提交 */}
          <div className="flex justify-end pb-4">
            <Button
              type="submit"
              disabled={submitting}
              className="h-9 gap-x-2 rounded-full px-5 text-sm font-semibold"
            >
              {submitting ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  生成中…
                </>
              ) : (
                <>
                  生成字幕
                  <Sparkle className="size-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      <Dialog open={styleDialogOpen} onOpenChange={setStyleDialogOpen}>
        <DialogContent
          className="w-[min(92vw,800px)] max-w-[800px] gap-0 overflow-hidden border-border bg-card p-0 shadow-2xl sm:rounded-2xl"
        >
          <DialogHeader className="border-b border-border px-6 py-4 pr-14">
            <DialogTitle className="text-base font-semibold">选择字幕样式</DialogTitle>
          </DialogHeader>

          <ScrollArea className="h-[min(70vh,560px)] px-6 py-4">
            <div className="flex flex-wrap justify-center gap-3 pb-2">
              {captionLibraryStyles.map((style) => (
                <CaptionStyleLibraryCard
                  key={style.key}
                  style={style}
                  selected={pendingStyle === style.key}
                  onSelect={setPendingStyle}
                />
              ))}
            </div>
          </ScrollArea>

          <div className="flex items-center justify-end gap-3 border-t border-border bg-card px-6 py-3">
            <Button
              type="button"
              variant="ghost"
              className="h-9 rounded-full px-5 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setStyleDialogOpen(false)}
            >
              取消
            </Button>
            <Button
              type="button"
              className="h-9 rounded-full px-6 text-sm font-semibold"
              onClick={() => {
                setSelectedStyle(pendingStyle)
                setStyleDialogOpen(false)
              }}
            >
              确认选择
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
